import {
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useEventsEntryVisible } from '../features/events/useEventsEntryVisible';
import { useGroupOnboarding } from '../features/groupOnboarding/api';
import { api, apiEndpoints, getCurrentStoreId } from '../utils/apiService';

/**
 * Which navigation items the signed-in user may see. Both sidebars read this
 * hook so a gate is written once: `AppSidebar` (legacy) and `AppFrameV2`.
 */
export type NavVisibility = {
  isPlatformAdmin: boolean;
  role?: 'Owner' | 'Manager' | 'Staff';
  /** SMS inbox (`/sms`). */
  showSms: boolean;
  /** Menu availability (`/menu-availability`). */
  showMenuAvailability: boolean;
  /** Legacy events inquiries (`/special-events`). */
  showEvents: boolean;
  /** Catering inquiries (`/catering`). */
  showCatering: boolean;
  /** Events platform (`/events`) — driven by the events session, not a flag. */
  showEventsPlatform: boolean;
  /** Group onboarding (`/onboarding`): owners, admins and managers of a group. */
  showGroupOnboarding: boolean;
  /** Questions Arthur couldn't answer (`/knowledge-questions`). */
  showKnowledgeQuestions: boolean;
  /** Additional FAQs can be published to Arthur (role still applies). */
  faqPublishEnabled: boolean;
  /**
   * The store flags are still loading. Flags without a cached-user fallback
   * (knowledge questions, FAQ publishing) read `false` until then.
   */
  flagsPending: boolean;
};

type StoreFlags = {
  showSms?: boolean;
  showMenuAvailability?: boolean;
  showEvents?: boolean;
  showCatering?: boolean;
  showKnowledgeQuestions?: boolean;
  faqPublishEnabled?: boolean;
};

const FLAG_FIELDS: Array<[keyof StoreFlags, string]> = [
  ['showSms', 'show_sms_on_dashboard'],
  ['showMenuAvailability', 'manual_menu_snoozing_enabled'],
  ['showEvents', 'takes_events'],
  ['showCatering', 'takes_catering'],
  ['showKnowledgeQuestions', 'knowledge_questions_enabled'],
  ['faqPublishEnabled', 'additional_faqs_publish_enabled'],
];

/** Fills only the flags this payload actually states, leaving the rest open. */
function readFlags(payload: unknown, into: StoreFlags) {
  const source = (payload ?? {}) as Record<string, unknown>;
  for (const [key, field] of FLAG_FIELDS) {
    if (into[key] === undefined && typeof source[field] === 'boolean') {
      into[key] = source[field] as boolean;
    }
  }
}

const allResolved = (flags: StoreFlags) =>
  FLAG_FIELDS.every(([key]) => flags[key] !== undefined);

async function readJson(response: Response | null) {
  if (!response?.ok) return null;
  return response.json().catch(() => null);
}

/**
 * `stores/user-info/` states the flags for the selected store, but older
 * deployments omit some of them; the store detail is the second source. Both
 * are best-effort — an unresolved flag falls back to the cached user in the
 * hook below, which is how the legacy sidebar behaved.
 */
async function fetchStoreFlags(storeId: string): Promise<StoreFlags> {
  const flags: StoreFlags = {};
  readFlags(
    await readJson(await api.get(apiEndpoints.getStoreUserInfo())),
    flags,
  );
  if (allResolved(flags) || !storeId) return flags;
  readFlags(
    await readJson(await api.get(apiEndpoints.getStoreById(storeId))),
    flags,
  );
  return flags;
}

const navVisibilityKey = (
  userId: number | string | undefined,
  storeId: string,
) => ['nav-visibility', userId ?? 'anonymous', storeId || 'no-store'];

/**
 * The events card just saved `takes_events`. Show events immediately, ahead of
 * the user-info refetch the settings event starts, so a figures read does not
 * rebuild the Off card against the old flag.
 */
export function rememberEventsEnabled(
  client: QueryClient,
  userId: number | string | undefined,
  storeId: string,
) {
  client.setQueryData<StoreFlags>(
    navVisibilityKey(userId, storeId),
    (current) => ({
      ...(current ?? {}),
      showEvents: true,
    }),
  );
}

/** The admin's last has-work answer for this group, until a fresh one. */
const rememberedWork = (key: string) => {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

export function useNavVisibility(): NavVisibility {
  const { user } = useAuth();
  const showEventsPlatform = useEventsEntryVisible();
  const queryClient = useQueryClient();
  const storeId =
    getCurrentStoreId() || (user?.store_id ? String(user.store_id) : '');

  const flagsQuery = useQuery({
    queryKey: navVisibilityKey(user?.id, storeId),
    queryFn: () => fetchStoreFlags(storeId),
    enabled: Boolean(user),
    staleTime: 60_000,
    retry: false,
  });
  const flags = flagsQuery.data;

  // A group's own people keep Onboarding; an admin gets it only while the
  // selected store's group still has something to complete, so the item
  // doesn't follow them into every finished group. The nav never waits on
  // the group: until the answer is in, the last one kept in localStorage
  // stands in, so the item doesn't pop in or out while it loads.
  const inGroup = (user?.onboarding_groups?.length ?? 0) > 0;
  const adminGroupId =
    user?.is_platform_admin === true && !inGroup
      ? (user?.store_group_id ?? null)
      : null;
  const adminGroup = useGroupOnboarding(adminGroupId);
  const workKey = `group-onboarding-nav:${user?.id}:${adminGroupId}`;
  // Optional chaining throughout: the nav must not crash on a payload
  // that isn't the shape it expects.
  const freshWork =
    (adminGroup.data?.stores?.some(
      (s) =>
        s.status !== 'live' ||
        (s.counts?.todo ?? 0) > 0 ||
        (s.counts?.waiting ?? 0) > 0,
    ) ??
      false) ||
    (adminGroup.data?.intakes?.some(
      (i) => i.state === 'open' || i.state === 'submitted',
    ) ??
      false);
  useEffect(() => {
    if (adminGroupId == null || adminGroup.data == null) return;
    try {
      localStorage.setItem(workKey, freshWork ? '1' : '0');
    } catch {
      // Private windows and blocked storage: the hint just isn't kept.
    }
  }, [adminGroupId, adminGroup.data, workKey, freshWork]);
  const groupWork =
    adminGroupId != null &&
    (adminGroup.data != null ? freshWork : rememberedWork(workKey));

  // Saving a store setting (here or in another tab) changes what the nav shows.
  useEffect(() => {
    if (!user) return;
    const refresh = () =>
      queryClient.invalidateQueries({
        queryKey: navVisibilityKey(user.id, storeId),
      });
    window.addEventListener('storage', refresh);
    window.addEventListener('store-settings-updated', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('store-settings-updated', refresh);
    };
  }, [queryClient, user, storeId]);

  return {
    isPlatformAdmin: user?.is_platform_admin === true,
    role: user?.role,
    showSms: flags?.showSms ?? user?.show_sms_on_dashboard === true,
    showMenuAvailability:
      flags?.showMenuAvailability ??
      user?.manual_menu_snoozing_enabled === true,
    showEvents: flags?.showEvents ?? user?.takes_events === true,
    showCatering: flags?.showCatering ?? user?.takes_catering === true,
    showEventsPlatform,
    showGroupOnboarding: inGroup || groupWork,
    showKnowledgeQuestions: flags?.showKnowledgeQuestions === true,
    faqPublishEnabled: flags?.faqPublishEnabled === true,
    flagsPending: Boolean(user) && flagsQuery.isPending,
  };
}
